import React from 'react';
import { 
  Users, 
  Phone, 
  MessageCircle, 
  Mail, 
  Calendar,
  DollarSign,
  Target,
  TrendingUp,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useGoogleSheets } from '../../hooks/useGoogleSheets';
import RecentLeadItem from './RecentLeadItem';

const DashboardPage: React.FC = () => {
  // Fetch consolidated dashboard data
  const { data: dashboardData, isLoading, error } = useGoogleSheets('dashboard');
  
  // Extract individual datasets from consolidated data
  const meetingsData = dashboardData?.meetings || [];
  const qualifiedData = dashboardData?.qualified || [];
  const callData = dashboardData?.['call-leads'] || [];
  const whatsappData = dashboardData?.['whatsapp-leads'] || [];
  const emailData = dashboardData?.['email-leads'] || [];
  const pipelineData = dashboardData?.['pipeline-leads'] || [];
  const preQualifiedData = dashboardData?.['pre-qualified'] || [];

  // Log pipeline data before filtering for recent leads
  console.log('[DashboardPage] pipelineData before filtering for recentLeads:', pipelineData);

  // Calculate key metrics
  const meetingsBooked = meetingsData.length;
  const pipelineGenerated = meetingsBooked * 20000; // Each meeting = $20K
  const qualifiedLeads = qualifiedData.length;
  const callsDone = callData.reduce((sum, lead) => sum + (parseInt(lead.call_attempts) || 0), 0);
  const whatsappsSent = whatsappData.reduce((sum, lead) => sum + (parseInt(lead.messages_sent) || 0), 0);
  const emailsSent = emailData.reduce((sum, lead) => sum + (parseInt(lead.emails_sent) || 0), 0);

  // Get recently engaged leads (last 6 with recent activity)
  const recentLeads = pipelineData
    .filter(lead => lead.last_reply_timestamp && lead.last_reply_timestamp !== 'N/A')
    .sort((a, b) => {
      const dateA = new Date(a.last_reply_timestamp || 0);
      const dateB = new Date(b.last_reply_timestamp || 0);
      return dateB.getTime() - dateA.getTime();
    })
    .slice(0, 6);

  console.log('[DashboardPage] recentLeads after filtering and sorting:', recentLeads);

  // Calculate agent performance metrics
  const preQualifiedLeads = preQualifiedData.length;
  const leadGenStats = {
    preQualified: preQualifiedLeads,
    qualified: qualifiedLeads,
    conversionRate: preQualifiedLeads > 0 ? Math.round((qualifiedLeads / preQualifiedLeads) * 100) : 0
  };

  const callAgentStats = {
    totalCalls: callsDone,
    connected: callData.reduce((sum, lead) => sum + (parseInt(lead.pickups) || 0), 0),
    booked: callData.filter(lead => lead.lead_status === 'booked').length,
    conversionRate: 0
  };
  callAgentStats.conversionRate = callAgentStats.connected > 0 ? Math.round((callAgentStats.booked / callAgentStats.connected) * 100) : 0;

  const whatsappAgentStats = {
    totalSent: whatsappsSent,
    replies: whatsappData.reduce((sum, lead) => sum + (parseInt(lead.replies_received) || 0), 0),
    activeChats: whatsappData.filter(lead => lead.lead_status === 'chatting').length,
    booked: whatsappData.filter(lead => lead.lead_status === 'booked').length,
    conversionRate: 0
  };
  whatsappAgentStats.conversionRate = whatsappAgentStats.replies > 0 ? Math.round((whatsappAgentStats.booked / whatsappAgentStats.replies) * 100) : 0;

  const emailAgentStats = {
    totalSent: emailsSent,
    replies: emailData.reduce((sum, lead) => sum + (parseInt(lead.replies_received) || 0), 0),
    activeConvos: emailData.filter(lead => lead.lead_status === 'talking').length,
    booked: emailData.filter(lead => lead.lead_status === 'booked').length,
    conversionRate: 0
  };
  emailAgentStats.conversionRate = emailAgentStats.replies > 0 ? Math.round((emailAgentStats.booked / emailAgentStats.replies) * 100) : 0;

  // Only show loading if we're loading AND have no data to display
  if (isLoading && (!dashboardData || Object.keys(dashboardData).length === 0)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-gray-500 text-lg mb-2">Loading dashboard...</div>
          <div className="text-gray-400 text-sm">Please Make Sure The System Is Connected</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection Status Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg flex items-center space-x-2 mb-6">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Top 6 Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Pipeline Generated */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="text-green-600" size={20} />
            </div>
          </div>
          <div className="text-2xl font-bold text-green-900 mb-1">
            ${(pipelineGenerated / 1000).toFixed(0)}K
          </div>
          <div className="text-sm text-green-700">Pipeline Generated</div>
        </div>

        {/* Meetings Booked */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="text-blue-600" size={20} />
            </div>
          </div>
          <div className="text-2xl font-bold text-blue-900 mb-1">{meetingsBooked}</div>
          <div className="text-sm text-blue-700">Meetings Booked</div>
        </div>

        {/* Qualified Leads */}
        <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl border border-purple-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Target className="text-purple-600" size={20} />
            </div>
          </div>
          <div className="text-2xl font-bold text-purple-900 mb-1">{qualifiedLeads}</div>
          <div className="text-sm text-purple-700">Qualified Leads</div>
        </div>

        {/* Calls Done */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Phone className="text-orange-600" size={20} />
            </div>
          </div>
          <div className="text-2xl font-bold text-orange-900 mb-1">{callsDone}</div>
          <div className="text-sm text-orange-700">Calls Done</div>
        </div>

        {/* WhatsApps Sent */}
        <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl border border-green-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <MessageCircle className="text-green-600" size={20} />
            </div>
          </div>
          <div className="text-2xl font-bold text-green-900 mb-1">{whatsappsSent}</div>
          <div className="text-sm text-green-700">WhatsApps Sent</div>
        </div>

        {/* Emails Sent */}
        <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Mail className="text-red-600" size={20} />
            </div>
          </div>
          <div className="text-2xl font-bold text-red-900 mb-1">{emailsSent}</div>
          <div className="text-sm text-red-700">Emails Sent</div>
        </div>
      </div>

      {/* Two Big Boxes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recently Engaged Leads */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-orange-100 rounded-lg flex items-center justify-center">
                <Users className="text-orange-600" size={14} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Recently Engaged Leads</h3>
            </div>
            <Link 
              to="/app/pipeline" 
              className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
            >
              <span>View in Pipeline</span>
              <ArrowRight size={14} />
            </Link>
          </div>

          <div className="space-y-2">
            {recentLeads.length > 0 ? (
              recentLeads.map((lead, index) => (
                <RecentLeadItem key={lead._rowIndex || index} lead={lead} />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">No Recent Engagement</p>
                <p className="text-sm">Leads will appear here once they start engaging with your campaigns</p>
              </div>
            )}
          </div>
        </div>

        {/* Agent Performance */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="text-blue-600" size={14} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Agent Performance</h3>
            </div>
          </div>

          <div className="space-y-6">
            {/* Lead Gen Agent */}
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="text-purple-600" size={16} />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Lead Gen Agent</h4>
                  <p className="text-sm text-gray-600">
                    {leadGenStats.preQualified} pre-qualified • {leadGenStats.qualified} qualified
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-purple-900">{leadGenStats.conversionRate}%</div>
                <div className="text-xs text-purple-700">conversion rate</div>
              </div>
            </div>

            {/* Call Agent */}
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Phone className="text-blue-600" size={16} />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Call Agent</h4>
                  <p className="text-sm text-gray-600">
                    {callAgentStats.totalCalls} calls • {callAgentStats.connected} connected
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-blue-900">{callAgentStats.conversionRate}%</div>
                <div className="text-xs text-blue-700">conversion rate</div>
              </div>
            </div>

            {/* WhatsApp Agent */}
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <MessageCircle className="text-green-600" size={16} />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">WhatsApp Agent</h4>
                  <p className="text-sm text-gray-600">
                    {whatsappAgentStats.totalSent} sent • {whatsappAgentStats.replies} replies
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-green-900">{whatsappAgentStats.conversionRate}%</div>
                <div className="text-xs text-green-700">conversion rate</div>
              </div>
            </div>

            {/* Email Agent */}
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <Mail className="text-red-600" size={16} />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Email Agent</h4>
                  <p className="text-sm text-gray-600">
                    {emailAgentStats.totalSent} sent • {emailAgentStats.replies} replies
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-red-900">{emailAgentStats.conversionRate}%</div>
                <div className="text-xs text-red-700">conversion rate</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Last Updated */}
      <div className="flex items-center justify-end">
        <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;